import React, { useEffect, useRef, useState } from 'react';
import {
  ShieldCheck, Leaf, Tag, HeartHandshake, Sparkles, ArrowDown,
} from 'lucide-react';

/**
 * Premium "About EntoRid" section.
 * - Scroll-triggered one-time animations via IntersectionObserver
 * - Animated background blobs + soft gradients
 * - 4 floating glassmorphism cards with breathing idle animation,
 *   hover lift, gradient-border glow, icon scale.
 * - Fully mobile-first: single column → 2-up → 4-up.
 */

const FEATURE_CARDS = [
  {
    Icon: ShieldCheck,
    title: 'Certified & Experienced Technicians',
    desc: 'Govt-licensed experts with 15+ years of field expertise across Uttarakhand.',
    accent: 'from-blue-500 to-indigo-500',
    iconBg: 'from-blue-50 to-indigo-50',
    iconText: 'text-blue-600',
  },
  {
    Icon: Leaf,
    title: 'Safe & Eco-Friendly Solutions',
    desc: 'WHO/CIB-approved chemicals — odourless, biodegradable, safe for kids & pets.',
    accent: 'from-emerald-500 to-green-500',
    iconBg: 'from-emerald-50 to-green-50',
    iconText: 'text-emerald-600',
  },
  {
    Icon: Tag,
    title: 'Transparent Pricing & Reliable Service',
    desc: 'Real-time quotes, no hidden charges, on-time arrival every single visit.',
    accent: 'from-amber-500 to-orange-500',
    iconBg: 'from-amber-50 to-orange-50',
    iconText: 'text-amber-600',
  },
  {
    Icon: HeartHandshake,
    title: 'Customer-First Guarantee',
    desc: 'Free re-treatment if pests return within warranty — no questions asked.',
    accent: 'from-rose-500 to-pink-500',
    iconBg: 'from-rose-50 to-pink-50',
    iconText: 'text-rose-600',
  },
];

/** One-shot intersection observer hook — adds `inView` class once visible. */
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, inView]);
  return [ref, inView];
}

export default function AboutUs() {
  const [headRef, headIn] = useReveal();
  const [statementRef, statementIn] = useReveal();
  const [paraRef, paraIn] = useReveal();
  const [gridRef, gridIn] = useReveal(0.1);
  const [closingRef, closingIn] = useReveal();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white py-16 sm:py-20 lg:py-28"
      style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
    >
      {/* Animated background blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-blue-500/5 blur-3xl animate-blob-slow" />
        <div className="absolute top-1/3 -right-32 w-[380px] h-[380px] rounded-full bg-emerald-500/8 blur-3xl animate-blob-slower" />
        <div className="absolute -bottom-40 left-1/3 w-[480px] h-[480px] rounded-full bg-indigo-500/5 blur-3xl animate-blob-slow" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Eyebrow + Headline */}
        <div
          ref={headRef}
          className={`text-center max-w-3xl mx-auto transition-all duration-[900ms] ease-out ${
            headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span
            data-testid="about-eyebrow"
            className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-100 px-3.5 py-1.5 rounded-full"
          >
            <Sparkles size={12} /> About EntoRid
          </span>

          <h2
            data-testid="about-headline"
            className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight"
          >
            Built on Trust.{' '}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-blue-700 via-indigo-600 to-emerald-600 bg-clip-text text-transparent">
                Powered by Precision.
              </span>
              <span
                aria-hidden
                className={`absolute left-0 -bottom-1 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500 rounded-full transition-all duration-[1100ms] delay-300 ease-out ${
                  headIn ? 'w-full opacity-100' : 'w-0 opacity-0'
                }`}
              />
            </span>
          </h2>
        </div>

        {/* Main statement */}
        <p
          ref={statementRef}
          data-testid="about-statement"
          className={`mt-7 sm:mt-8 max-w-3xl mx-auto text-center text-base sm:text-lg lg:text-xl text-slate-700 leading-relaxed transition-all duration-[900ms] delay-150 ease-out ${
            statementIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          At <span className="font-semibold text-slate-900">EntoRid</span>, we are committed to delivering safe, effective, and reliable pest control solutions that protect your home and business. With a focus on quality, professionalism, and customer satisfaction, we ensure every service meets the highest standards.
        </p>

        {/* Support paragraph */}
        <p
          ref={paraRef}
          data-testid="about-support"
          className={`mt-5 sm:mt-6 max-w-2xl mx-auto text-center text-sm sm:text-base text-slate-600 leading-relaxed transition-all duration-[900ms] delay-200 ease-out ${
            paraIn ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
          }`}
        >
          Our team of trained professionals uses advanced techniques and government-approved treatments to eliminate pests while maintaining safety for your family and environment. We believe in not just solving problems, but providing long-term protection and peace of mind.
        </p>

        {/* Gradient divider */}
        <div className="mt-10 sm:mt-12 mx-auto max-w-md h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />

        {/* Floating feature cards */}
        <div
          ref={gridRef}
          data-testid="about-cards"
          className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
        >
          {FEATURE_CARDS.map((card, i) => (
            <FeatureCard
              key={card.title}
              {...card}
              show={gridIn}
              index={i}
            />
          ))}
        </div>

        {/* Closing line */}
        <div
          ref={closingRef}
          data-testid="about-closing-wrap"
          className={`mt-12 sm:mt-16 max-w-3xl mx-auto text-center transition-all duration-[900ms] ease-out ${
            closingIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <p
            data-testid="about-closing"
            className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 leading-snug"
          >
            EntoRid stands for{' '}
            <span className="bg-gradient-to-r from-blue-700 to-emerald-600 bg-clip-text text-transparent">
              trust, precision, and lasting protection
            </span>
            <span className="text-slate-900"> — because your safety is our priority.</span>
          </p>

          {/* Subtle scroll cue */}
          <a
            href="#services"
            data-testid="about-scroll-cue"
            className="mt-7 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-emerald-700 hover:text-emerald-800 group"
          >
            Explore our services
            <ArrowDown size={14} className="transition-transform group-hover:translate-y-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ Icon, title, desc, accent, iconBg, iconText, show, index }) {
  return (
    <article
      data-testid={`about-card-${index + 1}`}
      style={{ transitionDelay: show ? `${index * 90}ms` : '0ms' }}
      className={`group relative rounded-2xl bg-white/70 backdrop-blur-sm ring-1 ring-slate-200 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)] hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.22)] hover:-translate-y-2 transition-all duration-300 overflow-hidden about-breathe transform-gpu ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Animated gradient border on hover */}
      <span
        aria-hidden
        className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none p-[1.5px] bg-gradient-to-br ${accent}`}
        style={{
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      <div className="relative p-5 sm:p-6">
        {/* Icon */}
        <div
          className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${iconBg} ring-1 ring-slate-100 ${iconText} shadow-sm transition-transform duration-300 group-hover:scale-105`}
        >
          <Icon size={24} strokeWidth={1.6} />
        </div>

        <h3 className="mt-4 text-sm sm:text-base font-bold text-slate-900 leading-snug">
          {title}
        </h3>
        <p className="mt-1.5 text-xs sm:text-[13px] text-slate-600 leading-relaxed">
          {desc}
        </p>

        {/* Subtle bottom accent line */}
        <span
          aria-hidden
          className={`block mt-4 h-[2px] w-8 group-hover:w-16 rounded-full bg-gradient-to-r ${accent} transition-all duration-500`}
        />
      </div>
    </article>
  );
}
